'use client';

/**
 * FeeOverridesEditor — configure per-org transaction service fees (donation /
 * disbursement / card) that feed services/fee_resolver.py on the backend.
 *
 * Lives in the RevOps Pricing tab. With no override, fees fall back to env defaults,
 * so this is purely additive. `billing_model = invoiced` suppresses the at-source fee
 * (double-billing guard). Uses the same fetch/auth pattern as TierConfigurator.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Save, DollarSign } from 'lucide-react';

const PLATFORM_BACKEND_URL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_PLATFORM_API_URL) ||
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_WEAVEIN_API_URL) ||
  'https://weavein-api.onrender.com';

function getAuthHeaders(): Record<string, string> {
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  const cboId = typeof window !== 'undefined' ? localStorage.getItem('active_cbo_id') : null;
  const tenantSlug = typeof window !== 'undefined' ? localStorage.getItem('platform_tenant_slug') : null;
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) h['Authorization'] = `Bearer ${token}`;
  if (cboId) h['X-CBO-ID'] = cboId;
  if (tenantSlug) h['X-Tenant-ID'] = tenantSlug;
  return h;
}

interface Org { id: number; name: string }
interface FeeOverride {
  id: string;
  cbo_id: number;
  org_name: string | null;
  service_type: string;
  fee_type: string;
  fee_percent: number;
  fee_fixed_cents: number;
  billing_model: string;
  notes: string | null;
}

const SERVICE_TYPES = [
  { value: 'donation_txn', label: 'Donations & grants' },
  { value: 'disbursement', label: 'Card disbursements' },
  { value: 'card_program', label: 'Card program' },
];
const FEE_TYPES = [
  { value: 'percent_plus_fixed', label: 'Percent + fixed' },
  { value: 'percent', label: 'Percent only' },
  { value: 'flat', label: 'Flat fee' },
];
const BILLING_MODELS = [
  { value: 'at_source', label: 'At source (collect on transaction)' },
  { value: 'invoiced', label: 'Invoiced (no at-source fee)' },
];

export default function FeeOverridesEditor() {
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [overrides, setOverrides] = useState<FeeOverride[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const [cboId, setCboId] = useState<number | undefined>(undefined);
  const [serviceType, setServiceType] = useState('donation_txn');
  const [feeType, setFeeType] = useState('percent_plus_fixed');
  const [feePercentPct, setFeePercentPct] = useState('2'); // shown as %, stored as fraction
  const [feeFixedCents, setFeeFixedCents] = useState('30');
  const [billingModel, setBillingModel] = useState('at_source');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [orgRes, ovRes] = await Promise.all([
        fetch(`${PLATFORM_BACKEND_URL}/api/cbos`, { headers: getAuthHeaders() }),
        fetch(`${PLATFORM_BACKEND_URL}/api/platform/v1/revops/fee-overrides`, { headers: getAuthHeaders() }),
      ]);
      const orgData = orgRes.ok ? await orgRes.json() : [];
      const ovData = ovRes.ok ? await ovRes.json() : { fee_overrides: [] };
      setOrgs((Array.isArray(orgData) ? orgData : orgData.cbos || []).map((o: any) => ({ id: o.id, name: o.name })));
      setOverrides(ovData.fee_overrides || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load fee overrides');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setError(null);
    setOk(null);
    if (!cboId) { setError('Select an organization'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${PLATFORM_BACKEND_URL}/api/platform/v1/revops/fee-overrides`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          cbo_id: cboId,
          service_type: serviceType,
          fee_type: feeType,
          fee_percent: (parseFloat(feePercentPct) || 0) / 100, // % -> fraction
          fee_fixed_cents: parseInt(feeFixedCents, 10) || 0,
          billing_model: billingModel,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.detail || `HTTP ${res.status}`);
      setOk('Fee override saved');
      await load();
    } catch (e: any) {
      setError(e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="mb-4 flex items-center gap-2">
        <DollarSign className="h-5 w-5 text-indigo-600" />
        <h3 className="text-lg font-semibold text-gray-900">Transaction Fees</h3>
      </div>
      <p className="mb-4 text-sm text-gray-500">
        Per-organization service fees for donations/grants, card disbursements, and card programs.
        With no override, fees use the platform default. <strong>Invoiced</strong> billing suppresses
        the at-source fee (the org is billed separately) to avoid double-charging.
      </p>

      {error && <div className="mb-3 rounded bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
      {ok && <div className="mb-3 rounded bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-700">{ok}</div>}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Organization</span>
          <select className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            value={cboId ?? ''} onChange={(e) => setCboId(e.target.value ? Number(e.target.value) : undefined)}>
            <option value="">Select…</option>
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Flow</span>
          <select className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            value={serviceType} onChange={(e) => setServiceType(e.target.value)}>
            {SERVICE_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Fee type</span>
          <select className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            value={feeType} onChange={(e) => setFeeType(e.target.value)}>
            {FEE_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Percent (%)</span>
          <input type="number" step="0.01" min="0" className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            value={feePercentPct} onChange={(e) => setFeePercentPct(e.target.value)} disabled={feeType === 'flat'} />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Fixed (cents)</span>
          <input type="number" min="0" className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            value={feeFixedCents} onChange={(e) => setFeeFixedCents(e.target.value)} disabled={feeType === 'percent'} />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Billing model</span>
          <select className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            value={billingModel} onChange={(e) => setBillingModel(e.target.value)}>
            {BILLING_MODELS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </label>
      </div>

      <div className="mt-4">
        <button onClick={save} disabled={saving}
          className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save fee override
        </button>
      </div>

      <div className="mt-6">
        <h4 className="mb-2 text-sm font-semibold text-gray-700">Current overrides</h4>
        {loading ? (
          <div className="flex items-center text-sm text-gray-500"><Loader2 className="mr-2 h-4 w-4 animate-spin" />Loading…</div>
        ) : overrides.length === 0 ? (
          <p className="text-sm text-gray-400">No per-org overrides — all orgs use platform defaults.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-gray-400">
                  <th className="py-2 pr-4">Organization</th>
                  <th className="py-2 pr-4">Flow</th>
                  <th className="py-2 pr-4">Fee</th>
                  <th className="py-2 pr-4">Billing</th>
                </tr>
              </thead>
              <tbody>
                {overrides.map((o) => (
                  <tr key={o.id} className="border-t border-gray-100">
                    <td className="py-2 pr-4">{o.org_name || `cbo ${o.cbo_id}`}</td>
                    <td className="py-2 pr-4">{SERVICE_TYPES.find((s) => s.value === o.service_type)?.label || o.service_type}</td>
                    <td className="py-2 pr-4">
                      {o.fee_type !== 'flat' && `${(o.fee_percent * 100).toFixed(2)}%`}
                      {o.fee_type === 'percent_plus_fixed' && ' + '}
                      {o.fee_type !== 'percent' && `$${(o.fee_fixed_cents / 100).toFixed(2)}`}
                    </td>
                    <td className="py-2 pr-4">
                      <span className={o.billing_model === 'invoiced' ? 'text-amber-600' : 'text-gray-700'}>
                        {o.billing_model}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
